import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Receipt, 
  User, 
  Calendar, 
  DollarSign, 
  Eye, 
  Search, 
  AlertCircle,
  FileText,
  Building,
  Mail,
  Phone,
  RefreshCw,
  Lock,
  Database,
  Shield,
  CheckCircle,
  Loader,
  X,
  Plus,
  Printer,
  Share2,
  Edit3,
  Download,
  Send,
  Filter,
  SortDesc,
  MoreVertical,
  AlertTriangle,
  Info,
  Star,
  TrendingUp,
  Clock,
  ExternalLink,
  Gift,
  Tag,
  Percent,
  MapPin
} from 'lucide-react';

// Import the SHARED Firebase Invoice Service
import {
  getInvoices,
  subscribeToInvoices,
  updateInvoice as updateInvoiceService,
  addInvoice,
  setCompanyContext,
  getReservations,
  subscribeToReservations,
  db
} from '../services/firebaseInvoiceService';

import SharedInvoicePreviewPage from './SharedInvoicePreviewPage';

// Custom motorbike icon component
const MotorbikeIcon = ({ size = 16, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8 5h8l2 4h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1a3 3 0 0 1-3 3 3 3 0 0 1-3-3H7a3 3 0 0 1-3 3 3 3 0 0 1-3-3V7a2 2 0 0 1 2-2h5zm0 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm8 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"></path>
  </svg>
);

// ENHANCED INVOICE MANAGER COMPONENT (inner component) - WITH DISCOUNT SUPPORT
const EnhancedInvoiceManagerCore = ({ 
  currentUser = { id: 'demo-user', username: 'demo_admin', role: 'admin' },
  currentCompany = { 
    name: 'EliteRide Motorcycles',
    address: '123 Elite Street, Motor City, MC 12345',
    phone: '(555) 123-4567',
    email: 'info@eliteride.com',
    website: 'www.eliteride.com'
  },
  getCompanyContext = () => 'demo-company-123',
  hasPermission = () => true,
  onNavigateToInvoicePreview = null
}) => {
  // State Management
  const [invoices, setInvoices] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operationLoading, setOperationLoading] = useState(new Set());
  const [extendedInvoiceStats, setExtendedInvoiceStats] = useState({
    count: 0,
    totalAmount: 0,
    averageAmount: 0
  });
  const [discountStats, setDiscountStats] = useState({
    count: 0,
    totalSavings: 0,
    averageSavings: 0
  });

  // Get company context
  const companyId = getCompanyContext ? getCompanyContext() : 'demo-company-123';

  // Enhanced Amount Getter for Invoices
  const getInvoiceAmount = useCallback((invoice) => {
    return parseFloat(
      invoice.totalAmount || 
      invoice.rentalAmount || 
      invoice.extendedAmount || 
      invoice.baseCost || 
      invoice.totalCost || 
      0
    );
  }, []);

  // Helper function to format bike display (model + number)
  const formatBikeDisplay = useCallback((invoice) => {
    const model = invoice.bikeModel;
    const number = invoice.motorbikeNumber;
    
    if (model && number) {
      return `${model} - ${number}`;
    }
    return number || model || 'N/A';
  }, []);

  // Enhanced helper functions
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Print Handler - WITH FULL DISCOUNT SUPPORT
  const handlePrintInvoice = async (invoice) => {
    try {
      console.log('Opening print preview for invoice:', invoice.invoiceNumber);
      
      setOperationLoading(prev => new Set(prev).add(invoice.id + '_print'));
      
      // Safely handle deposit amount
      let depositValue = 0;
      if (invoice.depositAmount) {
        const cleanDeposit = invoice.depositAmount.toString().replace(/[$]/g, '');
        depositValue = parseFloat(cleanDeposit) || 0;
      }

      // 💰 Calculate amounts WITH DISCOUNT
      const rentalAmount = parseFloat(invoice.rentalAmount || invoice.totalAmount) || 0;
      const addonTotal = invoice.selectedAddons?.reduce((sum, addon) => 
        sum + parseFloat(addon.calculatedCost || addon.price || 0), 0) || 0;
      
      // Read discount data from invoice
      const subtotalAmount = parseFloat(invoice.subtotalAmount) || (rentalAmount + addonTotal);
      const discountAmount = parseFloat(invoice.discountAmount) || 0;
      const hasDiscount = invoice.hasDiscount || discountAmount > 0;
      const discountData = invoice.discountData || null;
      
      // Calculate final total with discount
      const totalAmount = hasDiscount 
        ? Math.max(0, subtotalAmount - discountAmount)
        : parseFloat(invoice.totalAmount) || subtotalAmount;
      
      const isExtendedInvoice = invoice.isExtendedRental || invoice.type === 'extended';
      const currentDate = new Date();
      const invoiceNumber = invoice.invoiceNumber || `INV-${Date.now()}`;
      const reservationNumber = invoice.reservationNumber || '';

      // Calculate rental days
      const calculateDays = () => {
        if (!invoice.pickupDateTime || !invoice.dropoffDateTime) return 1;
        const pickup = new Date(invoice.pickupDateTime);
        const dropoff = new Date(invoice.dropoffDateTime);
        const pickupDate = new Date(pickup.getFullYear(), pickup.getMonth(), pickup.getDate());
        const dropoffDate = new Date(dropoff.getFullYear(), dropoff.getMonth(), dropoff.getDate());
        const diffTime = dropoffDate.getTime() - pickupDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return Math.max(diffDays, 1);
      };

      const rentalDays = calculateDays();
      const dailyRate = rentalAmount / rentalDays;
      const bikeDisplay = formatBikeDisplay(invoice);
      
      // Create formatted invoice HTML WITH DISCOUNT SECTION
      const invoiceHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice ${invoiceNumber}</title>
          <meta charset="utf-8">
          <style>
            @page { 
              size: A4; 
              margin: 0.5in;
            }
            body { 
              font-family: Arial, sans-serif; 
              line-height: 1.4; 
              color: #000;
              background: white;
            }
            .invoice-header { display: flex; justify-content: space-between; margin-bottom: 2rem; align-items: flex-start; }
            .company-info h1 { font-size: 1.5rem; margin: 0; color: #1f2937; }
            .invoice-details { text-align: right; }
            .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 2rem; }
            .section { border: 1px solid #e5e5e5; padding: 1rem; border-radius: 0.5rem; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 2rem; }
            th, td { border: 1px solid #e5e5e5; padding: 0.75rem; text-align: left; }
            th { background: #f5f5f5; font-weight: bold; }
            .total-section { display: grid; grid-template-columns: 1fr 300px; gap: 2rem; }
            .total-box { border: 2px solid #1f2937; padding: 1.5rem; text-align: center; }
            .total-amount { font-size: 2rem; font-weight: bold; margin: 1rem 0; }
            .footer { margin-top: 2rem; padding-top: 1rem; border-top: 1px solid #e5e5e5; font-size: 0.9rem; color: #666; }
            .extended-notice { background: #fef2f2; border: 1px solid #fca5a5; padding: 1rem; border-radius: 0.5rem; margin-bottom: 2rem; }
            .extended-title { color: #dc2626; font-weight: bold; margin: 0 0 0.5rem 0; }
            .extended-text { color: #991b1b; font-size: 0.9rem; margin: 0; }
            .reservation-badge {
              display: inline-block;
              background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
              color: white;
              padding: 0.5rem 1rem;
              border-radius: 0.5rem;
              font-weight: bold;
              margin: 0.5rem 0;
              border: 2px solid #ea580c;
            }
            .booking-ref {
              background: #fff7ed;
              border: 1px solid #f97316;
              padding: 0.5rem;
              border-radius: 0.25rem;
              color: #ea580c;
              font-weight: 600;
              font-size: 0.875rem;
              margin: 0.5rem 0;
            }
            .discount-box {
              background: linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%);
              border: 2px solid #86efac;
              padding: 1rem;
              border-radius: 0.5rem;
              margin-top: 1rem;
            }
            .discount-title {
              color: #15803d;
              font-weight: bold;
              margin: 0 0 0.5rem 0;
              font-size: 1rem;
            }
            .discount-text {
              color: #166534;
              font-size: 0.875rem;
              margin: 0.25rem 0;
            }
            .discount-amount {
              color: #15803d;
              font-weight: bold;
              font-size: 1.1rem;
              margin-top: 0.5rem;
            }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            <div class="company-info">
              <h1>${currentCompany?.name || 'EliteRide Motorcycles'}</h1>
              <p style="margin: 0.5rem 0; color: #6b7280;">Premium Motorcycle Rentals</p>
              <p style="margin: 0.25rem 0; color: #6b7280;">${currentCompany?.address || '123 Elite Street, Motor City, MC 12345'}</p>
              <p style="margin: 0.25rem 0; color: #6b7280;">${currentCompany?.phone || '(555) 123-4567'}</p>
              <p style="margin: 0.25rem 0; color: #6b7280;">${currentCompany?.email || 'info@eliteride.com'}</p>
            </div>
            <div class="invoice-details">
              <h2 style="color: ${isExtendedInvoice ? '#dc2626' : '#1f2937'}; margin: 0 0 1rem 0;">
                ${isExtendedInvoice ? 'EXTENDED INVOICE' : 'INVOICE'}
              </h2>
              <p><strong>Invoice #:</strong> ${invoiceNumber}</p>
              ${reservationNumber ? `
                <div class="reservation-badge">
                  📄 Booking #: ${reservationNumber}
                </div>
              ` : ''}
              <p><strong>Date:</strong> ${currentDate.toLocaleDateString()}</p>
              <p><strong>Due Date:</strong> ${new Date(currentDate.getTime() + 30*24*60*60*1000).toLocaleDateString()}</p>
              ${isExtendedInvoice && invoice.daysOverdue ? `
                <p style="color: #dc2626; font-weight: bold; margin-top: 0.5rem;">
                  ${invoice.daysOverdue} Days Overdue
                </p>
              ` : ''}
            </div>
          </div>
          
          ${isExtendedInvoice ? `
            <div class="extended-notice">
              <h3 class="extended-title">Extended Rental Charges</h3>
              <p class="extended-text">
                This invoice covers extended period charges beyond the original rental agreement.
                ${reservationNumber ? `<br><strong>Original Booking:</strong> ${reservationNumber}` : ''}
                ${invoice.daysOverdue && invoice.dailyRateApplied ? 
                  `<br><strong>Calculation: $${invoice.dailyRateApplied} × ${invoice.daysOverdue} days = $${totalAmount.toFixed(2)}</strong>` 
                  : ''}
              </p>
            </div>
          ` : ''}
          
          <div class="grid">
            <div class="section">
              <h3 style="margin: 0 0 1rem 0; color: #1f2937;">Bill To</h3>
              <p style="margin: 0.5rem 0; font-weight: bold;">${invoice.customerName || 'N/A'}</p>
              <p style="margin: 0.25rem 0; color: #6b7280;">${invoice.email || invoice.customerEmail || 'N/A'}</p>
              <p style="margin: 0.25rem 0; color: #6b7280;">${invoice.customerPhone || 'N/A'}</p>
              <p style="margin: 0.25rem 0; color: #6b7280;">License: ${invoice.licenseNumber || 'N/A'}</p>
              ${reservationNumber ? `
                <p class="booking-ref">📄 Booking: ${reservationNumber}</p>
              ` : ''}
            </div>
            <div class="section">
              <h3 style="margin: 0 0 1rem 0; color: #1f2937;">Vehicle Information</h3>
              <p style="margin: 0.5rem 0; font-weight: bold;">${bikeDisplay}</p>
              ${reservationNumber ? `
                <p class="booking-ref">📄 Booking: ${reservationNumber}</p>
              ` : ''}
              <p style="margin: 0.25rem 0; color: #6b7280;">Pickup: ${invoice.pickupLocation || 'Main Location'}</p>
              <p style="margin: 0.25rem 0; color: #6b7280;">Duration: ${rentalDays} day${rentalDays !== 1 ? 's' : ''}</p>
              <p style="margin: 0.25rem 0; color: #6b7280;">Rate: $${dailyRate.toFixed(2)}/day</p>
            </div>
            <div class="section">
              <h3 style="margin: 0 0 1rem 0; color: #1f2937;">Rental Period</h3>
              <p style="margin: 0.5rem 0;"><strong>Pickup:</strong><br>${formatDate(invoice.pickupDateTime)}</p>
              <p style="margin: 0.5rem 0;"><strong>Return:</strong><br>${formatDate(invoice.dropoffDateTime)}</p>
              ${isExtendedInvoice && invoice.originalReturnDate ? `
                <p style="margin: 0.5rem 0; color: #dc2626;"><strong>Original Due:</strong><br>${formatDate(invoice.originalReturnDate)}</p>
              ` : ''}
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Quantity</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>${isExtendedInvoice ? 'Extended Rental Charges' : 'Motorcycle Rental'}</strong><br>
                  <span style="color: #6b7280;">${bikeDisplay}</span>
                  ${reservationNumber ? `<br><span style="color: #ea580c; font-weight: 600; font-size: 0.875rem;">Booking: ${reservationNumber}</span>` : ''}<br>
                  <small style="color: #9ca3af;">${isExtendedInvoice ? 'Additional charges for overdue period' : 'Premium motorcycle rental service'}</small>
                </td>
                <td>${isExtendedInvoice && invoice.daysOverdue ? `${invoice.daysOverdue} day${invoice.daysOverdue !== 1 ? 's' : ''}` : `${rentalDays} day${rentalDays !== 1 ? 's' : ''}`}</td>
                <td>$${isExtendedInvoice && invoice.dailyRateApplied ? invoice.dailyRateApplied.toFixed(2) : dailyRate.toFixed(2)}</td>
                <td><strong>$${rentalAmount.toFixed(2)}</strong></td>
              </tr>
              ${invoice.selectedAddons?.map(addon => `
                <tr>
                  <td>
                    <strong>${addon.name || 'Add-on Service'}</strong><br>
                    <span style="color: #6b7280;">${addon.category || 'Additional Service'}</span>
                  </td>
                  <td>${addon.pricingMethod === 'perDay' ? `${rentalDays} day${rentalDays !== 1 ? 's' : ''}` : '1x'}</td>
                  <td>$${parseFloat(addon.price || 0).toFixed(2)}</td>
                  <td><strong>$${parseFloat(addon.calculatedCost || addon.price || 0).toFixed(2)}</strong></td>
                </tr>
              `).join('') || ''}
            </tbody>
          </table>
          
          <div class="total-section">
            <div>
              <h3 style="color: #1f2937;">Payment Terms</h3>
              <p><strong>Payment Method:</strong> ${invoice.paymentMethod || 'Cash/Card'}</p>
              <p><strong>Due Date:</strong> ${new Date(currentDate.getTime() + 30*24*60*60*1000).toLocaleDateString()}</p>
              <p><strong>Deposit:</strong> $${depositValue.toFixed(2)} (Applied)</p>
              ${reservationNumber ? `
                <p style="color: #ea580c; font-weight: 600;">
                  <strong>Booking Reference:</strong> ${reservationNumber}
                </p>
              ` : ''}
              ${hasDiscount && discountData ? `
                <div class="discount-box">
                  <p class="discount-title">💰 Discount Applied</p>
                  ${discountData.selectedDiscount ? `
                    <p class="discount-text">
                      <strong>${discountData.selectedDiscount.name}</strong>
                      ${discountData.selectedDiscount.code ? ` (${discountData.selectedDiscount.code})` : ''}
                    </p>
                    ${discountData.selectedDiscount.description ? `
                      <p class="discount-text">${discountData.selectedDiscount.description}</p>
                    ` : ''}
                  ` : `
                    <p class="discount-text">
                      <strong>Custom ${discountData.customDiscountType} discount</strong>
                    </p>
                  `}
                  ${discountData.reason ? `
                    <p class="discount-text"><strong>Reason:</strong> ${discountData.reason}</p>
                  ` : ''}
                  <p class="discount-amount">Total Savings: $${discountAmount.toFixed(2)}</p>
                </div>
              ` : ''}
              ${invoice.note ? `<p><strong>Notes:</strong> ${invoice.note}</p>` : ''}
            </div>
            <div class="total-box">
              <h3 style="margin: 0 0 1rem 0; color: #1f2937;">${isExtendedInvoice ? 'Extended Charges Total' : 'Invoice Summary'}</h3>
              ${!isExtendedInvoice ? `
                <p style="margin: 0.5rem 0;">Rental: $${rentalAmount.toFixed(2)}</p>
                ${addonTotal > 0 ? `<p style="margin: 0.5rem 0;">Add-ons: $${addonTotal.toFixed(2)}</p>` : ''}
                <p style="margin: 0.5rem 0; padding-top: 0.5rem; border-top: 1px solid #e5e5e5;">
                  <strong>Subtotal: $${subtotalAmount.toFixed(2)}</strong>
                </p>
                ${hasDiscount ? `
                  <p style="margin: 0.5rem 0; color: #dc2626; font-weight: bold;">
                    Discount: -$${discountAmount.toFixed(2)}
                  </p>
                ` : ''}
              ` : ''}
              <div class="total-amount" style="color: ${isExtendedInvoice ? '#dc2626' : '#059669'};">
                TOTAL: $${totalAmount.toFixed(2)}
              </div>
              <p><strong>${invoice.status === 'paid' ? 'PAYMENT RECEIVED' : 'PAYMENT DUE'}</strong></p>
            </div>
          </div>
          
          <div class="footer">
            <p><strong>Generated by:</strong> ${currentUser?.username || 'System Administrator'} | 
               <strong>Generated on:</strong> ${currentDate.toLocaleString()} | 
               <strong>Source:</strong> Invoice Manager</p>
            ${reservationNumber ? `<p style="color: #ea580c; font-weight: 600;">Booking Reference: ${reservationNumber}</p>` : ''}
            ${hasDiscount ? `<p style="color: #15803d; font-weight: 600;">Customer Saved: $${discountAmount.toFixed(2)}</p>` : ''}
            <p>This is a computer-generated invoice. No signature required.</p>
            <p>Questions? Contact us at ${currentCompany?.email || 'support@eliteride.com'} or ${currentCompany?.phone || '(555) 123-4567'}</p>
          </div>
        </body>
        </html>
      `;
      
      // Open print window with formatted invoice
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      if (!printWindow) {
        alert('Please allow popups for this site to print invoices');
        return;
      }
      
      printWindow.document.open();
      printWindow.document.write(invoiceHtml);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };
      
      console.log('Print preview opened successfully with discount data');
      
    } catch (error) {
      console.error('Error opening print preview:', error);
      alert('Error opening print preview: ' + error.message);
    } finally {
      setOperationLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id + '_print');
        return newSet;
      });
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      console.log('Opening PDF download dialog for invoice:', invoice.invoiceNumber);
      
      setOperationLoading(prev => new Set(prev).add(invoice.id + '_pdf'));
      
      const message = 'A print preview will open.\n\nTo save as PDF:\n' +
        '1. In the print dialog, select "Save as PDF" or "Microsoft Print to PDF"\n' +
        '2. Click Save/Print\n' +
        '3. Choose your save location\n\n' +
        'The invoice will be saved with professional formatting and discount details.';
      
      alert(message);
      await handlePrintInvoice(invoice);
      
    } catch (error) {
      console.error('Error opening PDF dialog:', error);
      alert('Error: ' + error.message);
    } finally {
      setOperationLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id + '_pdf');
        return newSet;
      });
    }
  };

  // Data loading effect with discount stats
  useEffect(() => {
    if (!currentCompany || !currentUser || !companyId) {
      console.log('EnhancedInvoiceManager: Missing context');
      setIsLoading(false);
      return;
    }

    console.log('EnhancedInvoiceManager: Loading data for company:', currentCompany.name);

    if (db) {
      setCompanyContext(companyId, currentCompany.name);
    }

    if (db) {
      const unsubscribeInvoices = subscribeToInvoices(
        (invoicesData) => {
          let extendedCount = 0;
          let extendedTotal = 0;
          let discountCount = 0;
          let discountTotal = 0;
          
          invoicesData.forEach((invoice) => {
            // Extended invoice stats
            if (invoice.isExtendedRental || invoice.type === 'extended') {
              extendedCount++;
              const extendedAmount = parseFloat(
                invoice.totalAmount || 
                invoice.rentalAmount || 
                invoice.extendedAmount || 
                0
              );
              extendedTotal += extendedAmount;
            }
            
            // 💰 Discount stats
            if (invoice.hasDiscount || parseFloat(invoice.discountAmount) > 0) {
              discountCount++;
              discountTotal += parseFloat(invoice.discountAmount) || 0;
            }
          });
          
          setExtendedInvoiceStats({
            count: extendedCount,
            totalAmount: extendedTotal,
            averageAmount: extendedCount > 0 ? extendedTotal / extendedCount : 0
          });
          
          setDiscountStats({
            count: discountCount,
            totalSavings: discountTotal,
            averageSavings: discountCount > 0 ? discountTotal / discountCount : 0
          });
          
          console.log(`Real-time invoices update: ${invoicesData.length} invoices (${extendedCount} extended, ${discountCount} with discounts, $${discountTotal.toFixed(2)} total savings) for ${currentCompany.name}`);
          setInvoices(invoicesData);
          setIsLoading(false);
          setError(null);
        },
        companyId
      );

      const unsubscribeReservations = subscribeToReservations(
        (reservationsData) => {
          setReservations(reservationsData);
          console.log('Real-time reservations update:', reservationsData.length, 'reservations for linking');
        },
        companyId
      );

      return () => {
        unsubscribeInvoices();
        unsubscribeReservations();
        console.log('EnhancedInvoiceManager: Cleaned up Firebase listeners for company:', currentCompany.name);
      };
    } else {
      setTimeout(() => {
        console.log('No Firebase connection - empty invoice list');
        setInvoices([]);
        setReservations([]);
        setIsLoading(false);
        setError(null);
      }, 500);
    }
  }, [currentCompany, currentUser, companyId]);

  // Enhanced filter and sort with discount search
  useEffect(() => {
    let filtered = [...invoices];

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(invoice => 
        (invoice.invoiceNumber || '').toLowerCase().includes(search) ||
        (invoice.customerName || '').toLowerCase().includes(search) ||
        (invoice.customerEmail || invoice.email || '').toLowerCase().includes(search) ||
        (invoice.motorbikeNumber || '').toLowerCase().includes(search) ||
        (invoice.bikeModel || '').toLowerCase().includes(search) ||
        (invoice.reservationNumber || '').toLowerCase().includes(search) ||
        // 💰 DISCOUNT SEARCH
        (invoice.discountData?.selectedDiscount?.name || '').toLowerCase().includes(search) ||
        (invoice.discountData?.selectedDiscount?.code || '').toLowerCase().includes(search) ||
        (invoice.discountData?.reason || '').toLowerCase().includes(search)
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === filterStatus);
    }

    if (filterType !== 'all') {
      if (filterType === 'extended') {
        filtered = filtered.filter(invoice => invoice.isExtendedRental || invoice.type === 'extended');
      } else if (filterType === 'regular') {
        filtered = filtered.filter(invoice => !invoice.isExtendedRental && invoice.type !== 'extended');
      } else if (filterType === 'automatic') {
        filtered = filtered.filter(invoice => invoice.automaticallyGenerated);
      } else if (filterType === 'discounted') {
        filtered = filtered.filter(invoice => invoice.hasDiscount || parseFloat(invoice.discountAmount) > 0);
      }
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case 'amount':
          return getInvoiceAmount(b) - getInvoiceAmount(a);
        case 'customer':
          return (a.customerName || '').localeCompare(b.customerName || '');
        case 'invoice':
          return (a.invoiceNumber || '').localeCompare(b.invoiceNumber || '');
        case 'type':
          return (a.type || '').localeCompare(b.type || '');
        default:
          return 0;
      }
    });

    setFilteredInvoices(filtered);
  }, [invoices, searchTerm, filterStatus, filterType, sortBy, getInvoiceAmount]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'generated': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'sent': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'paid': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'overdue': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getTypeColor = (invoice) => {
    if (invoice.isExtendedRental || invoice.type === 'extended') {
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    }
    if (invoice.automaticallyGenerated) {
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
    return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  };

  const getTypeLabel = (invoice) => {
    if (invoice.isExtendedRental || invoice.type === 'extended') {
      return 'Extended';
    }
    if (invoice.automaticallyGenerated) {
      return 'Auto';
    }
    return 'Regular';
  };

  const getTotalValue = () => {
    return filteredInvoices.reduce((sum, invoice) => sum + getInvoiceAmount(invoice), 0);
  };

  const getEnhancedStatusCounts = () => {
    return {
      generated: invoices.filter(inv => inv.status === 'generated').length,
      sent: invoices.filter(inv => inv.status === 'sent').length,
      paid: invoices.filter(inv => inv.status === 'paid').length,
      overdue: invoices.filter(inv => inv.status === 'overdue').length,
      extended: invoices.filter(inv => inv.isExtendedRental || inv.type === 'extended').length,
      automatic: invoices.filter(inv => inv.automaticallyGenerated).length
    };
  };

  // ✅✅✅ FIXED: Enhanced view handler WITH ALL FIELDS INCLUDING DELIVERY CHARGES AND DEPOSIT DATA ✅✅✅
  const handleViewInvoice = (invoice) => {
    console.log('Navigating to shared invoice preview page:', invoice.invoiceNumber);
    
    const relatedReservation = reservations.find(r => r.id === invoice.reservationId || r.id === invoice.originalReservationId);
    
    const invoiceForPreview = {
      id: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      reservationNumber: invoice.reservationNumber,
      reservationId: invoice.reservationId,  // ✅ CRITICAL: Enables automatic document loading
      originalReservationId: invoice.originalReservationId,  // ✅ Fallback for document loading
      customerName: invoice.customerName,
      email: invoice.customerEmail || invoice.email,
      customerEmail: invoice.customerEmail || invoice.email,
      customerPhone: invoice.customerPhone,
      whatsappNumber: invoice.customerPhone || invoice.whatsappNumber,
      motorbikeNumber: invoice.motorbikeNumber,
      bikeModel: invoice.bikeModel,
      
      // 💰 COMPLETE DISCOUNT DATA
      rentalAmount: invoice.rentalAmount || invoice.totalAmount,
      subtotalAmount: invoice.subtotalAmount || invoice.rentalAmount || invoice.totalAmount,
      discountAmount: invoice.discountAmount || '0',
      totalAmount: invoice.totalAmount,
      hasDiscount: invoice.hasDiscount || false,
      discountData: invoice.discountData || null,
      
      // 📄 DOCUMENT DATA - COMPLETE SECTION
      uploadedDocuments: invoice.uploadedDocuments || {},
      documentRequirements: invoice.documentRequirements || [],
      documentValidationStatus: invoice.documentValidationStatus || 'pending',
      documentsStoredInFirebase: invoice.documentsStoredInFirebase || false,
      documentCount: invoice.documentCount || 0,
      
      // 🗺️ DELIVERY CHARGES DATA - COMPLETE SECTION (THIS WAS MISSING!)
      deliveryCharges: invoice.deliveryCharges || 0,
      pickupLocation: invoice.pickupLocation || 'Not specified',
      dropoffLocation: invoice.dropoffLocation || 'Not specified',
      pickupLocationDetails: invoice.pickupLocationDetails || null,
      dropoffLocationDetails: invoice.dropoffLocationDetails || null,
      
      // 💵 DEPOSIT DATA - COMPLETE SECTION (THIS WAS MISSING!)
      depositAmount: invoice.depositAmount,
      depositData: invoice.depositData || null,
      depositCurrency: invoice.depositCurrency || null,
      depositSymbol: invoice.depositSymbol || null,
      
      // OTHER INVOICE DATA
      selectedAddons: invoice.selectedAddons || [],
      pickupDateTime: invoice.pickupDateTime || invoice.pickupDate,
      dropoffDateTime: invoice.dropoffDateTime || invoice.dropoffDate,
      status: invoice.status,
      rentalPaymentStatus: invoice.status,
      paymentMethod: invoice.paymentMethod,
      licenseNumber: invoice.licenseNumber,
      note: invoice.note,
      isExtendedRental: invoice.isExtendedRental,
      automaticallyGenerated: invoice.automaticallyGenerated,
      type: invoice.type,
      daysOverdue: invoice.daysOverdue,
      dailyRateApplied: invoice.dailyRateApplied,
      originalReturnDate: invoice.originalReturnDate,
      actualReturnDate: invoice.actualReturnDate,
      processedBy: invoice.createdByUsername || invoice.processedBy
    };
    
    console.log('💰 Passing discount data to preview:', {
      hasDiscount: invoiceForPreview.hasDiscount,
      discountAmount: invoiceForPreview.discountAmount,
      discountData: invoiceForPreview.discountData
    });
    
    console.log('📄 Passing document data to preview:', {
      uploadedDocuments: Object.keys(invoiceForPreview.uploadedDocuments).length,
      documentCount: invoiceForPreview.documentCount,
      documentsStoredInFirebase: invoiceForPreview.documentsStoredInFirebase,
      reservationId: invoiceForPreview.reservationId,
      originalReservationId: invoiceForPreview.originalReservationId
    });
    
    console.log('🗺️ Passing delivery charges to preview:', {
      deliveryCharges: invoiceForPreview.deliveryCharges,
      pickupLocation: invoiceForPreview.pickupLocation,
      dropoffLocation: invoiceForPreview.dropoffLocation,
      pickupLocationDetails: invoiceForPreview.pickupLocationDetails,
      dropoffLocationDetails: invoiceForPreview.dropoffLocationDetails
    });
    
    console.log('💵 Passing deposit data to preview:', {
      depositAmount: invoiceForPreview.depositAmount,
      depositData: invoiceForPreview.depositData,
      depositCurrency: invoiceForPreview.depositCurrency,
      depositSymbol: invoiceForPreview.depositSymbol
    });
    
    if (onNavigateToInvoicePreview) {
      onNavigateToInvoicePreview({
        invoice: invoiceForPreview,
        sourceComponent: 'invoice-manager',
        currentUser,
        currentCompany
      });
    } else {
      console.warn('onNavigateToInvoicePreview function not provided');
      alert('Navigation to invoice preview not configured. Please set up the navigation handler.');
    }
  };

  const handleUpdateInvoiceStatus = async (invoice, newStatus) => {
    const invoiceId = invoice.id;
    setOperationLoading(prev => new Set(prev).add(invoiceId));

    try {
      console.log('Updating invoice status:', invoice.invoiceNumber, 'to', newStatus);
      
      if (db) {
        await updateInvoiceService(invoiceId, {
          status: newStatus,
          updatedBy: currentUser?.id,
          updatedByUsername: currentUser?.username
        });
      } else {
        await new Promise(resolve => setTimeout(resolve, 500));
        setInvoices(prev => prev.map(inv => 
          inv.id === invoiceId 
            ? { ...inv, status: newStatus, updatedAt: new Date().toISOString() }
            : inv
        ));
      }
      
      console.log('Invoice status updated successfully');
      
    } catch (error) {
      console.error('Error updating invoice status:', error);
      alert('Error updating invoice status: ' + error.message);
    } finally {
      setOperationLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });
    }
  };

  const handleShare = async (invoice) => {
    const baseUrl = window.location.hostname === 'localhost' 
      ? 'https://easyrentaly.com' 
      : window.location.origin;
    
    const shareableLink = invoice.shareableLink || `${baseUrl}/invoice-preview/${invoice.id}`;
    const bikeInfo = formatBikeDisplay(invoice);
    const bookingRef = invoice.reservationNumber ? ` - Booking ${invoice.reservationNumber}` : '';
    const discountInfo = invoice.hasDiscount && parseFloat(invoice.discountAmount) > 0 
      ? ` (Saved $${parseFloat(invoice.discountAmount).toFixed(2)})` 
      : '';
    const shareText = `Invoice ${invoice.invoiceNumber || 'N/A'}${bookingRef} - ${invoice.customerName || 'Customer'} - ${bikeInfo} - $${(invoice.totalAmount || 0).toFixed(2)}${discountInfo}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Invoice ${invoice.invoiceNumber || 'N/A'}${bookingRef}`,
          text: shareText,
          url: shareableLink
        });
        console.log('Invoice shared successfully');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          fallbackCopy(shareableLink, shareText);
        }
      }
    } else {
      fallbackCopy(shareableLink, shareText);
    }
  };

  const fallbackCopy = async (link, text) => {
    try {
      await navigator.clipboard.writeText(`${text}\n\n${link}`);
      alert('Invoice details copied to clipboard!\n\n' + text + '\n\n' + link);
    } catch (error) {
      alert(`Invoice Details:\n\n${text}\n\n${link}\n\nPlease copy manually.`);
    }
  };

  // Export invoices as CSV in the same column format as the invoice import template.
  // Each invoice gets one header row (customer, discount, deposit, status) plus one row
  // per extra line item (add-ons, pickup/drop-off charges) sharing the invoice number.
  const handleExportInvoices = () => {
    if (filteredInvoices.length === 0) {
      alert('No invoices to export');
      return;
    }

    const headers = [
      'Invoice number', 'Date', 'Customer', 'Phone', 'Email', 'Country', 'ID number',
      'Description', 'Qty', 'Unit price', 'Amount', 'Discount', 'Deposit', 'Status', 'Notes'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const toNumber = (value) => parseFloat(String(value ?? '').replace(/[^0-9.-]/g, '')) || 0;
    const round2 = (value) => Math.round(value * 100) / 100;

    const toDate = (value) => {
      if (!value) return null;
      const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
      return isNaN(date.getTime()) ? null : date;
    };

    const formatIsoDate = (value) => {
      const date = toDate(value);
      if (!date) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    };

    // "20 - 23 Dec" or "28 Dec - 2 Jan"
    const formatDateRange = (start, end) => {
      const from = toDate(start);
      const to = toDate(end);
      if (!from) return '';
      const month = (d) => d.toLocaleDateString('en-GB', { month: 'short' });
      if (!to) return `${from.getDate()} ${month(from)}`;
      return from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()
        ? `${from.getDate()} - ${to.getDate()} ${month(to)}`
        : `${from.getDate()} ${month(from)} - ${to.getDate()} ${month(to)}`;
    };

    const countDays = (start, end) => {
      const from = toDate(start);
      const to = toDate(end);
      if (!from || !to) return 1;
      const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate());
      const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate());
      return Math.max(Math.floor((toDay - fromDay) / (1000 * 60 * 60 * 24)) + 1, 1);
    };

    // Use Qty x Unit price when they reproduce the amount exactly, otherwise just the Amount
    const buildLine = (description, qty, unitPrice, amount) => {
      const total = round2(amount);
      if (qty > 0 && unitPrice > 0 && round2(qty * round2(unitPrice)) === total) {
        return { description, qty, unitPrice: round2(unitPrice), amount: '' };
      }
      return { description, qty: '', unitPrice: '', amount: total };
    };

    const rows = [];

    filteredInvoices.forEach((invoice) => {
      const pickup = invoice.pickupDateTime || invoice.pickupDate;
      const dropoff = invoice.dropoffDateTime || invoice.dropoffDate;
      const isExtended = invoice.isExtendedRental || invoice.type === 'extended';
      const rentalAmount = toNumber(invoice.rentalAmount || invoice.totalAmount);

      const bike = invoice.bikeModel && invoice.motorbikeNumber
        ? `${invoice.bikeModel} (${invoice.motorbikeNumber})`
        : (invoice.bikeModel || invoice.motorbikeNumber || 'Rental');
      const rentalDescription = [bike, formatDateRange(pickup, dropoff), isExtended ? '(extended)' : '']
        .filter(Boolean).join(' ');

      const rentalQty = isExtended && invoice.daysOverdue ? invoice.daysOverdue : countDays(pickup, dropoff);
      const rentalUnit = isExtended && invoice.dailyRateApplied
        ? toNumber(invoice.dailyRateApplied)
        : rentalAmount / rentalQty;

      const lines = [buildLine(rentalDescription, rentalQty, rentalUnit, rentalAmount)];

      (invoice.selectedAddons || []).forEach((addon) => {
        const qty = addon.pricingMethod === 'perDay' ? countDays(pickup, dropoff) : 1;
        const amount = toNumber(addon.calculatedCost || addon.price);
        lines.push(buildLine(addon.name || 'Add-on', qty, toNumber(addon.price), amount));
      });

      const pickupCharge = toNumber(invoice.pickupLocationDetails?.deliveryCharge);
      const dropoffCharge = toNumber(invoice.dropoffLocationDetails?.deliveryCharge);
      if (pickupCharge > 0) {
        const location = invoice.pickupLocationDetails?.name || invoice.pickupLocation;
        lines.push(buildLine(`Pickup: ${location || 'Delivery'}`, 1, pickupCharge, pickupCharge));
      }
      if (dropoffCharge > 0) {
        const location = invoice.dropoffLocationDetails?.name || invoice.dropoffLocation;
        lines.push(buildLine(`Drop-off: ${location || 'Delivery'}`, 1, dropoffCharge, dropoffCharge));
      }
      const deliveryTotal = toNumber(invoice.deliveryCharges);
      if (pickupCharge === 0 && dropoffCharge === 0 && deliveryTotal > 0) {
        lines.push(buildLine('Delivery charges', 1, deliveryTotal, deliveryTotal));
      }

      const discount = toNumber(invoice.discountAmount);
      const deposit = toNumber(invoice.depositAmount);

      lines.forEach((line, index) => {
        const isFirst = index === 0;
        rows.push([
          invoice.invoiceNumber,
          isFirst ? formatIsoDate(invoice.createdAt || pickup) : '',
          isFirst ? invoice.customerName : '',
          isFirst ? (invoice.customerPhone || invoice.whatsappNumber) : '',
          isFirst ? (invoice.customerEmail || invoice.email) : '',
          isFirst ? (invoice.country || invoice.nationality) : '',
          isFirst ? (invoice.idNumber || invoice.passportNumber || invoice.licenseNumber) : '',
          line.description,
          line.qty,
          line.unitPrice,
          line.amount,
          isFirst && discount > 0 ? round2(discount) : '',
          isFirst && deposit > 0 ? round2(deposit) : '',
          isFirst ? (invoice.status === 'paid' ? 'Paid' : 'Unpaid') : '',
          isFirst ? invoice.note : ''
        ].map(escapeCsv).join(','));
      });
    });

    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoices-export-${formatIsoDate(new Date())}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleRefresh = async () => {
    setLastRefresh(new Date());
    console.log('Manual refresh triggered for invoices');
    
    if (db) {
      setIsLoading(true);
      try {
        const invoices = await getInvoices(companyId);
        setInvoices(invoices);
        console.log(`Refreshed: ${invoices.length} invoices loaded`);
      } catch (error) {
        console.error('Error refreshing invoices:', error);
        setError('Failed to refresh: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        console.log('Mock data refreshed');
      }, 500);
    }
  };

  // Permission check
  if (!hasPermission('invoice')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto text-center py-8 sm:py-12 px-4">
          <Shield className="mx-auto h-16 w-16 sm:h-20 sm:w-20 text-gray-400 mb-4 sm:mb-6" />
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-300 mb-3 sm:mb-4">Access Denied</h3>
          <p className="text-gray-500 text-base sm:text-lg mb-2">You don't have permission to access enhanced invoice management.</p>
          <p className="text-gray-600 text-xs sm:text-sm">Contact your company administrator for access.</p>
          <div className="mt-4 sm:mt-6 bg-blue-900/30 border border-blue-700 rounded-lg p-3 sm:p-4 max-w-md mx-auto">
            <div className="flex items-center text-blue-300 text-xs sm:text-sm mb-1">
              <Building className="mr-2 flex-shrink-0" size={16} />
              <span className="truncate">Company: {currentCompany?.name}</span>
            </div>
            <div className="flex items-center text-blue-300 text-xs sm:text-sm">
              <User className="mr-2 flex-shrink-0" size={16} />
              <span className="truncate">User: {currentUser?.username} ({currentUser?.role})</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-64">
          <div className="text-center px-4">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-white text-base sm:text-lg">Loading Enhanced Invoice Management...</p>
            <p className="text-gray-400 text-xs sm:text-sm mt-2">
              {currentCompany ? `Loading invoices for ${currentCompany.name}` : 'Loading company context...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-64">
          <div className="text-center px-4">
            <AlertCircle className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-red-400 mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Error Loading Invoices</h3>
            <p className="text-gray-300 text-sm sm:text-base mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 mx-auto text-sm sm:text-base"
            >
              <RefreshCw size={16} />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusCounts = getEnhancedStatusCounts();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Enhanced Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 gap-4">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white flex items-center flex-wrap">
            <Receipt size={24} className="text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-blue-500 to-purple-600">
              Enhanced Invoice Management
            </span>
            <div className="ml-0 sm:ml-4 mt-2 sm:mt-0 flex items-center text-green-400 text-xs sm:text-sm">
              <Lock size={14} className="mr-1" />
              <span>Multi-Tenant</span>
            </div>
          </h2>
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <div className="flex items-center text-blue-400 text-xs sm:text-sm">
              <Building size={14} className="mr-1" />
              <span className="truncate max-w-[150px] sm:max-w-none">{currentCompany?.name}</span>
            </div>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-xs sm:text-sm px-2 py-1"
              title="Refresh invoices"
            >
              <RefreshCw size={14} />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleExportInvoices}
              disabled={filteredInvoices.length === 0}
              className="flex items-center gap-1 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs sm:text-sm px-2 py-1"
              title="Export shown invoices as CSV"
            >
              <Download size={14} />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Main Interface */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
          
          {/* Enhanced Summary Cards WITH DISCOUNT STATS */}
          <div className="p-4 sm:p-6 border-b border-gray-700">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-300 text-xs sm:text-sm font-medium">Total Invoices</p>
                    <p className="text-xl sm:text-2xl font-bold text-white">{filteredInvoices.length}</p>
                  </div>
                  <Receipt className="w-6 h-6 sm:w-8 sm:h-8 text-green-400" />
                </div>
              </div>
              
              <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-xs sm:text-sm font-medium">Total Value</p>
                    <p className="text-xl sm:text-2xl font-bold text-white">${getTotalValue().toFixed(2)}</p>
                  </div>
                  <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                </div>
              </div>
              
              <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-300 text-xs sm:text-sm font-medium">Extended</p>
                    <p className="text-xl sm:text-2xl font-bold text-white">{statusCounts.extended}</p>
                  </div>
                  <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-400" />
                </div>
              </div>
              
              <div className="bg-purple-900/30 border border-purple-700/50 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-300 text-xs sm:text-sm font-medium">Auto Generated</p>
                    <p className="text-xl sm:text-2xl font-bold text-white">{statusCounts.automatic}</p>
                  </div>
                  <Star className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
                </div>
              </div>
              
              <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-300 text-xs sm:text-sm font-medium">Paid</p>
                    <p className="text-xl sm:text-2xl font-bold text-white">{statusCounts.paid}</p>
                  </div>
                  <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-400" />
                </div>
              </div>
              
              {/* 💰 NEW DISCOUNT STATS CARD */}
              <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-700/50 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-300 text-xs sm:text-sm font-medium">Discounts</p>
                    <p className="text-xl sm:text-2xl font-bold text-white">{discountStats.count}</p>
                    <p className="text-xs text-green-400 font-medium">${discountStats.totalSavings.toFixed(0)} saved</p>
                  </div>
                  <Gift className="w-6 h-6 sm:w-8 sm:h-8 text-green-400" />
                </div>
              </div>
            </div>

            {/* Enhanced Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1 w-full">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search by invoice #, booking #, customer, bike, or discount code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 sm:py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2.5 sm:py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">All Status</option>
                  <option value="generated">Generated</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>

                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2.5 sm:py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">All Types</option>
                  <option value="regular">Regular</option>
                  <option value="extended">Extended</option>
                  <option value="automatic">Auto Gen</option>
                  <option value="discounted">💰 With Discount</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2.5 sm:py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-green-500 col-span-2 sm:col-span-1"
                >
                  <option value="date">Date</option>
                  <option value="amount">Amount</option>
                  <option value="customer">Customer</option>
                  <option value="invoice">Invoice #</option>
                  <option value="type">Type</option>
                </select>
              </div>
            </div>
          </div>

          {/* Enhanced Invoice List WITH DISCOUNT BADGES */}
          <div className="max-h-[600px] sm:max-h-96 overflow-y-auto">
            {filteredInvoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 sm:p-12 text-gray-400">
                <Receipt size={40} className="mb-4 text-gray-500 sm:w-12 sm:h-12" />
                <p className="text-base sm:text-lg font-medium">No invoices found</p>
                <p className="text-xs sm:text-sm mt-2 text-gray-500 text-center px-4">
                  {invoices.length === 0 
                    ? `No invoices yet for ${currentCompany?.name}` 
                    : 'Try adjusting your filters'}
                </p>
                <div className="mt-4 text-xs text-blue-400 text-center px-4">
                  Enhanced system supports discounts, extended invoices, document uploads, and booking tracking
                </div>
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {filteredInvoices.map((invoice) => {
                  const isLoading = operationLoading.has(invoice.id);
                  const isPdfLoading = operationLoading.has(invoice.id + '_pdf');
                  const isPrintLoading = operationLoading.has(invoice.id + '_print');
                  const isExtendedInvoice = invoice.isExtendedRental || invoice.type === 'extended';
                  const isAutoGenerated = invoice.automaticallyGenerated;
                  const displayAmount = getInvoiceAmount(invoice);
                  const bikeDisplay = formatBikeDisplay(invoice);
                  
                  // 💰 Discount data
                  const hasDiscount = invoice.hasDiscount || parseFloat(invoice.discountAmount) > 0;
                  const discountAmount = parseFloat(invoice.discountAmount) || 0;
                  
                  // 📄 Document data
                  const hasDocuments = invoice.documentCount > 0 || (invoice.uploadedDocuments && Object.keys(invoice.uploadedDocuments).length > 0);
                  const documentCount = invoice.documentCount || (invoice.uploadedDocuments ? Object.keys(invoice.uploadedDocuments).length : 0);
                  
                  return (
                    <div
                      key={invoice.id}
                      className={`p-4 sm:p-6 hover:bg-gray-700/50 transition-colors cursor-pointer ${isExtendedInvoice ? 'bg-red-900/10 border-l-4 border-red-500' : ''}`}
                      onClick={() => handleViewInvoice(invoice)}
                    >
                      <div className="flex flex-col space-y-4">
                        
                        {/* Mobile: Stacked Layout WITH DISCOUNT AND DOCUMENTS */}
                        <div className="flex flex-col sm:hidden space-y-3">
                          {/* Invoice Header */}
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-10 h-10 ${isExtendedInvoice ? 'bg-red-500/20' : 'bg-green-500/20'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                <Receipt className={`w-5 h-5 ${isExtendedInvoice ? 'text-red-400' : 'text-green-400'}`} />
                              </div>
                              <div>
                                <h4 className="text-white font-medium text-sm font-mono">
                                  {invoice.invoiceNumber || 'N/A'}
                                </h4>
                                <p className="text-xs text-gray-400">
                                  {formatDate(invoice.createdAt).split(',')[0]}
                                </p>
                              </div>
                            </div>
                            <div className={`text-base font-bold ${isExtendedInvoice ? 'text-red-400' : 'text-green-400'}`}>
                              ${displayAmount.toFixed(2)}
                            </div>
                          </div>

                          {/* Status Badges WITH DISCOUNT AND DOCUMENTS */}
                          <div className="flex gap-2 flex-wrap">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full border ${getStatusColor(invoice.status || 'generated')}`}>
                              {invoice.status || 'generated'}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full border ${getTypeColor(invoice)}`}>
                              {getTypeLabel(invoice)}
                            </span>
                            {isExtendedInvoice && invoice.daysOverdue && (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                                {invoice.daysOverdue} days
                              </span>
                            )}
                            {/* 💰 DISCOUNT BADGE - MOBILE */}
                            {hasDiscount && (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30 gap-1">
                                <Gift size={10} />
                                Saved ${discountAmount.toFixed(2)}
                              </span>
                            )}
                            {/* 📄 DOCUMENT BADGE - MOBILE */}
                            {hasDocuments && (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30 gap-1">
                                <FileText size={10} />
                                {documentCount} doc{documentCount !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>

                          {/* Customer Info - MOBILE */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2">
                              <User className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                              <span className="text-sm text-white font-medium truncate">
                                {invoice.customerName || 'Unknown'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-300">
                              <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{invoice.customerEmail || invoice.email || 'N/A'}</span>
                            </div>
                            {invoice.reservationNumber && (
                              <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/40 rounded px-2 py-1.5">
                                <FileText className="w-3 h-3 text-orange-400 flex-shrink-0" />
                                <span className="text-xs text-orange-300 font-medium">Booking #</span>
                                <span className="text-xs text-orange-400 font-bold truncate">
                                  {invoice.reservationNumber}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-xs text-orange-300">
                              <MotorbikeIcon size={12} className="text-orange-400 flex-shrink-0" />
                              <span className="truncate font-medium">{bikeDisplay}</span>
                            </div>
                          </div>

                          {/* Dates */}
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center gap-1 text-green-300">
                              <Calendar className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{invoice.pickupDateTime ? new Date(invoice.pickupDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-1 text-red-300">
                              <Calendar className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{invoice.dropoffDateTime ? new Date(invoice.dropoffDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}</span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2">
                            <button
                              className="flex-1 p-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewInvoice(invoice);
                              }}
                              disabled={isLoading}
                            >
                              <ExternalLink size={14} />
                              <span>View</span>
                            </button>
                            
                            <button
                              className="flex-1 p-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadPDF(invoice);
                              }}
                              disabled={isPdfLoading}
                            >
                              {isPdfLoading ? (
                                <Loader size={14} className="animate-spin" />
                              ) : (
                                <>
                                  <Download size={14} />
                                  <span>PDF</span>
                                </>
                              )}
                            </button>
                            
                            <button
                              className="p-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePrintInvoice(invoice);
                              }}
                              disabled={isPrintLoading}
                              title="Print"
                            >
                              {isPrintLoading ? (
                                <Loader size={14} className="animate-spin" />
                              ) : (
                                <Printer size={14} />
                              )}
                            </button>
                            
                            <button
                              className="p-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare(invoice);
                              }}
                              disabled={isLoading}
                              title="Share Invoice"
                            >
                              <Share2 size={14} />
                            </button>
                          </div>
                        </div>

                        {/* Desktop: Grid Layout WITH DISCOUNT AND DOCUMENTS */}
                        <div className="hidden sm:grid sm:grid-cols-12 gap-4 items-center">
                        
                        {/* Enhanced Invoice Info */}
                        <div className="col-span-12 md:col-span-3">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`w-10 h-10 ${isExtendedInvoice ? 'bg-red-500/20' : 'bg-green-500/20'} rounded-lg flex items-center justify-center`}>
                              <Receipt className={`w-5 h-5 ${isExtendedInvoice ? 'text-red-400' : 'text-green-400'}`} />
                            </div>
                            <div>
                              <h4 className="text-white font-medium text-sm font-mono">
                                {invoice.invoiceNumber || 'N/A'}
                              </h4>
                              <p className="text-xs text-gray-400">
                                {formatDate(invoice.createdAt)}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full border ${getStatusColor(invoice.status || 'generated')}`}>
                              {invoice.status || 'generated'}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full border ${getTypeColor(invoice)}`}>
                              {getTypeLabel(invoice)}
                            </span>
                            {isAutoGenerated && (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                                Auto
                              </span>
                            )}
                            {isExtendedInvoice && invoice.daysOverdue && (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                                {invoice.daysOverdue} days
                              </span>
                            )}
                            {/* 💰 DISCOUNT BADGE - DESKTOP */}
                            {hasDiscount && (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30 gap-1">
                                <Gift size={10} />
                                ${discountAmount.toFixed(2)}
                              </span>
                            )}
                            {/* 📄 DOCUMENT BADGE - DESKTOP */}
                            {hasDocuments && (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30 gap-1">
                                <FileText size={10} />
                                {documentCount}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Customer & Vehicle - DESKTOP */}
                        <div className="col-span-12 md:col-span-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-blue-400" />
                              <span className="text-sm text-white font-medium">
                                {invoice.customerName || 'Unknown'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-300">
                              <Mail className="w-3 h-3 text-gray-400" />
                              <span className="truncate">{invoice.customerEmail || invoice.email || 'N/A'}</span>
                            </div>
                            {invoice.reservationNumber && (
                              <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/40 rounded px-2 py-1">
                                <FileText className="w-3 h-3 text-orange-400" />
                                <span className="text-xs text-orange-300 font-medium">Booking #</span>
                                <span className="text-xs text-orange-400 font-bold">
                                  {invoice.reservationNumber}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-xs text-orange-300">
                              <MotorbikeIcon className="w-3 h-3 text-orange-400" />
                              <span className="font-medium">{bikeDisplay}</span>
                            </div>
                            {isExtendedInvoice && (
                              <div className="flex items-center gap-2 text-xs text-red-300">
                                <Clock className="w-3 h-3 text-red-400" />
                                <span>Extended charges</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Rental Dates */}
                        <div className="col-span-12 md:col-span-3">
                          <div className="space-y-1 text-xs">
                            <div className="flex items-center gap-2 text-green-300">
                              <Calendar className="w-3 h-3" />
                              <span>Start: {invoice.pickupDateTime || invoice.pickupDate ? new Date(invoice.pickupDateTime || invoice.pickupDate).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-red-300">
                              <Calendar className="w-3 h-3" />
                              <span>End: {invoice.dropoffDateTime || invoice.dropoffDate ? new Date(invoice.dropoffDateTime || invoice.dropoffDate).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            {isExtendedInvoice && invoice.originalReturnDate && (
                              <div className="flex items-center gap-2 text-orange-300">
                                <AlertTriangle className="w-3 h-3" />
                                <span>Due: {new Date(invoice.originalReturnDate).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
                            <User className="w-3 h-3" />
                            <span>by {invoice.createdByUsername || invoice.processedBy || 'Unknown'}</span>
                          </div>
                        </div>

                        {/* Amount & Actions */}
                        <div className="col-span-12 md:col-span-2">
                          <div className="text-right">
                            <div className={`text-lg font-bold ${isExtendedInvoice ? 'text-red-400' : 'text-green-400'} mb-2`}>
                              ${displayAmount.toFixed(2)}
                            </div>
                            {hasDiscount && (
                              <div className="text-xs text-green-400 mb-1 flex items-center justify-end gap-1">
                                <Gift size={10} />
                                <span>Saved ${discountAmount.toFixed(2)}</span>
                              </div>
                            )}
                            {hasDocuments && (
                              <div className="text-xs text-purple-400 mb-1 flex items-center justify-end gap-1">
                                <FileText size={10} />
                                <span>{documentCount} doc{documentCount !== 1 ? 's' : ''}</span>
                              </div>
                            )}
                            {isExtendedInvoice && invoice.dailyRateApplied && invoice.daysOverdue && (
                              <div className="text-xs text-red-300 mb-2">
                                ${invoice.dailyRateApplied}/day × {invoice.daysOverdue}
                              </div>
                            )}
                            <div className="flex gap-1 justify-end flex-wrap">
                              <button
                                className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewInvoice(invoice);
                                }}
                                disabled={isLoading}
                                title="View Invoice"
                              >
                                <ExternalLink size={14} />
                                <span className="text-xs hidden lg:inline">View</span>
                              </button>
                              
                              <button
                                className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPDF(invoice);
                                }}
                                disabled={isPdfLoading}
                                title="Download PDF"
                              >
                                {isPdfLoading ? (
                                  <Loader size={14} className="animate-spin" />
                                ) : (
                                  <Download size={14} />
                                )}
                                <span className="text-xs hidden lg:inline">PDF</span>
                              </button>
                              
                              <button
                                className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePrintInvoice(invoice);
                                }}
                                disabled={isPrintLoading}
                                title="Print"
                              >
                                {isPrintLoading ? (
                                  <Loader size={14} className="animate-spin" />
                                ) : (
                                  <Printer size={14} />
                                )}
                              </button>
                              
                              <button
                                className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShare(invoice);
                                }}
                                disabled={isLoading}
                                title="Share Invoice"
                              >
                                <Share2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Status Summary WITH DISCOUNT AND DOCUMENT INFO */}
        <div className="mt-4 sm:mt-6 bg-gradient-to-r from-blue-900/20 to-green-900/20 border border-blue-700 rounded-lg p-3 sm:p-4 text-center">
          <div className="text-blue-300 text-xs sm:text-sm font-medium mb-1 flex items-center justify-center gap-2 flex-wrap">
            <Database size={14} className="text-green-400 flex-shrink-0" />
            <span>Enhanced Multi-Tenant Invoice Management System</span>
          </div>
          <div className="text-blue-400 text-xs leading-relaxed px-2">
            Multi-tenant security • Company isolation • Real-time sync • Professional PDF • Extended invoices • Discount tracking • Document uploads
            <br className="hidden sm:block" />
            <span className="block sm:inline mt-1 sm:mt-0">Booking numbers • Bike models • Discount search • Customer savings • Firebase Storage</span>
          </div>
          <div className="text-blue-500 text-xs mt-2 leading-relaxed px-2">
            <span className="inline-block mr-2">Total: <span className="font-bold">{invoices.length}</span></span> • 
            <span className="inline-block mx-2">Extended: <span className="font-bold text-red-400">{extendedInvoiceStats.count}</span> ($<span className="font-bold text-red-400">{extendedInvoiceStats.totalAmount.toFixed(2)}</span>)</span> • 
            <span className="inline-block mx-2">Discounts: <span className="font-bold text-green-400">{discountStats.count}</span> ($<span className="font-bold text-green-400">{discountStats.totalSavings.toFixed(0)} saved</span>)</span>
            <br className="sm:hidden" />
            <span className="inline-block mx-2">Value: <span className="font-bold">${getTotalValue().toFixed(2)}</span></span> • 
            <span className="inline-block mx-2">Company: <span className="font-bold">{currentCompany?.name}</span></span> •
            <span className="inline-block ml-2">Data: <span className="font-bold text-green-400">
              {db ? 'Firebase' : 'Demo'}
            </span></span>
          </div>
        </div>
      </div>
    </div>
  );
};

// MAIN WRAPPER COMPONENT WITH NAVIGATION STATE MANAGEMENT
const EnhancedInvoiceManager = (props) => {
  const [currentView, setCurrentView] = useState('invoice-manager');
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);

  const handleNavigateToInvoicePreview = (data) => {
    console.log('🧾 Navigating to invoice preview from invoice manager:', data);
    setSelectedInvoiceData(data);
    setCurrentView('invoice-preview');
  };

  const handleNavigateBackToInvoiceManager = () => {
    console.log('🔙 Navigating back to invoice manager');
    setCurrentView('invoice-manager');
    setSelectedInvoiceData(null);
  };

  if (currentView === 'invoice-preview' && selectedInvoiceData) {
    return (
      <SharedInvoicePreviewPage
        invoice={selectedInvoiceData.invoice}
        currentUser={selectedInvoiceData.currentUser}
        currentCompany={selectedInvoiceData.currentCompany}
        sourceComponent={selectedInvoiceData.sourceComponent}
        onNavigateBack={handleNavigateBackToInvoiceManager}
        getCompanyContext={props.getCompanyContext}
      />
    );
  }

  return (
    <EnhancedInvoiceManagerCore
      {...props}
      onNavigateToInvoicePreview={handleNavigateToInvoicePreview}
    />
  );
};

export default EnhancedInvoiceManager;